import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../styles/athlete-admin-layout.css";
import "../styles/calendar.css";
import "../styles/profile-form.css";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { type DateClickArg } from "@fullcalendar/interaction";
import type { DatesSetArg, EventClickArg } from "@fullcalendar/core";
import WeeklySnapshot from "../polaris-components/WeeklySnapshot";
import { API_BASE_URL } from "../utils/apiConfig";
import GoogleEventDetailsModal, { GoogleEventDetails } from "./GoogleEventDetailsModal";
import { useCalendar } from "../hooks/useCalendar";
import { calendarService, CalendarEvent } from "../services/calendarService";
import { sanitizeAndLinkifyDescription } from "../utils/htmlSanitizer";
import { isAthlete, isSignedInWithGoogle, isStaff, isAdmin, isCoach, isPolaris } from "../utils/auth";
import ComboBox, { ComboBoxOption, ComboBoxOptionGroup } from "./ComboBox";
import { fetchCurrentAthleteProfile, fetchSports, groupSportsByCategory, Sport } from "../utils/athleteApi";
import {
    buildRecruitingLegendItems,
    EMPTY_RECRUITING_MONTH_HINT,
    formatUsCalendarDate,
    inclusiveRecruitingEndIso,
    isNcaaRecruitingCatalogSport,
    recruitingCalendarLabel,
    RecruitingLegendItem,
    RecruitingNcaaDivision,
} from "../utils/recruitingCalendar";
import {
    apiEventFromCalendarClick,
    buildCalendarDisplayEvents,
    selectRecruitingEventForDate,
    utcDateFromCalendarClick,
} from "../utils/recruitingCalendarEvents";
import { useAuth } from "../contexts/AuthContext";
import { useAppFeatures } from "../hooks/useAppFeatures";

type ApiEvent = CalendarEvent;

type DraftEvent = {
    id?: string; // keep the "p:123" form so we detect source
    source?: "polaris" | "team" | "personal" | "company" | "recruiting";
    title: string;
    description?: string;
    allDay: boolean;
    start: string; // ISO
    end?: string;
};


const FMT_ISO = (d: Date) => d.toISOString();

function plusHours(date: Date, h: number) {
    const n = new Date(date);
    n.setHours(n.getHours() + h);
    return n;
}
function nextDay(date: Date) {
    const n = new Date(date);
    n.setDate(n.getDate() + 1);
    return n;
}
function isPolarisId(id?: string) {
    return !!id && id.startsWith("p:");
}
function numericIdOrNull(id?: string) {
    if (!id) return null;
    const m = id.match(/^p:(\d+)$/);
    return m ? Number(m[1]) : null;
}
function extractGoogleEventId(id?: string) {
    if (!id) return null;
    const m = id.match(/^g:(.+)$/);
    return m ? m[1] : null;
}

export default function CalendarPage() {
    const { googleConnected } = useAuth();
    const signedInWithGoogle = googleConnected || isSignedInWithGoogle();
    const athleteUser = isAthlete();
    const staffAdminUser = isStaff() || isAdmin() || isCoach() || isPolaris();
    const { features: appFeatures, loading: appFeaturesLoading } = useAppFeatures();
    // Wait for flags to load before showing the filter; fail open only on fetch error.
    const recruitingCalendarEnabled = appFeaturesLoading
        ? false
        : appFeatures === null
            ? true
            : appFeatures.recruitingCalendarEnabled;
    const canShowRecruiting = recruitingCalendarEnabled && (athleteUser || staffAdminUser);
    const [athleteSportId, setAthleteSportId] = useState<number | null>(null);
    const [athleteSportName, setAthleteSportName] = useState<string | null>(null);
    const [athleteSportReady, setAthleteSportReady] = useState(!athleteUser);
    const [selectedSportId, setSelectedSportId] = useState<number | null>(null);
    const [recruitingDivision, setRecruitingDivision] = useState<RecruitingNcaaDivision>("D1");
    const [sports, setSports] = useState<Sport[]>([]);
    const [sportsLoading, setSportsLoading] = useState(false);

    const sportComboGroups = useMemo(
        () =>
            groupSportsByCategory(sports.filter((sport) => isNcaaRecruitingCatalogSport(sport.category))).map(
                (group) => ({
                    label: group.category,
                    options: group.sports.map((sport) => ({
                        id: sport.id,
                        name: sport.name,
                    })),
                })
            ),
        [sports]
    );
    const sportComboOptions = useMemo(
        () => sportComboGroups.flatMap((group) => group.options),
        [sportComboGroups]
    );

    useEffect(() => {
        if (!athleteUser) {
            setAthleteSportId(null);
            setAthleteSportName(null);
            setAthleteSportReady(true);
            return;
        }
        setAthleteSportReady(false);
        fetchCurrentAthleteProfile()
            .then(async (profile) => {
                const sport = profile?.sport;
                if (typeof sport === "object" && sport?.id != null) {
                    setAthleteSportId(sport.id);
                    setAthleteSportName(sport.name ?? null);
                } else if (typeof sport === "string" && sport.trim().length > 0) {
                    const name = sport.trim();
                    setAthleteSportName(name);
                    const catalog = await fetchSports();
                    const match = catalog?.find((entry) => entry.name === name);
                    setAthleteSportId(match?.id ?? null);
                } else {
                    setAthleteSportId(null);
                    setAthleteSportName(null);
                }
            })
            .catch(() => {
                setAthleteSportId(null);
                setAthleteSportName(null);
            })
            .finally(() => setAthleteSportReady(true));
    }, [athleteUser]);

    useEffect(() => {
        if (!staffAdminUser) {
            return;
        }
        setSportsLoading(true);
        fetchSports()
            .then((data) => setSports(data ?? []))
            .finally(() => setSportsLoading(false));
    }, [staffAdminUser]);

    const recruitingSportId = athleteUser ? athleteSportId : selectedSportId;
    const selectedSportName =
        sports.find((sport) => sport.id === selectedSportId)?.name ?? null;
    const recruitingChipLabel = recruitingCalendarLabel(
        athleteUser ? athleteSportName : selectedSportName,
        recruitingDivision
    );

    const onRecruitingDivisionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value === "D1" || value === "D2") {
            setRecruitingDivision(value);
        }
    }, []);

    const {
        events: filteredEvents,
        recruitingEvents,
        isLoading,
        personalEventsError,
        companyEventsError,
        recruitingEventsError,
        recruitingEventsNotice,
        showPersonal,
        showCompany,
        showPolaris,
        showTeam,
        showRecruiting,
        setShowPersonal,
        setShowCompany,
        setShowPolaris,
        setShowTeam,
        setShowRecruiting,
        fetchEvents,
        refetchEvents,
    } = useCalendar({
        signedInWithGoogle,
        recruitingSportId: canShowRecruiting ? recruitingSportId : null,
        passRecruitingSportId: staffAdminUser,
        recruitingDivision,
    });

    useEffect(() => {
        if (!canShowRecruiting && showRecruiting) {
            setShowRecruiting(false);
        }
    }, [canShowRecruiting, showRecruiting, setShowRecruiting]);

    // modal + toast
    const [modalOpen, setModalOpen] = useState(false);
    const [modalReadonly, setModalReadonly] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<string>("");

    // Google event details modal
    const [googleEventModalOpen, setGoogleEventModalOpen] = useState(false);
    const [googleEventDetails, setGoogleEventDetails] = useState<GoogleEventDetails | null>(null);
    const [googleEventLoading, setGoogleEventLoading] = useState(false);

    const [draft, setDraft] = useState<DraftEvent>({
        title: "",
        description: "",
        allDay: true,
        start: FMT_ISO(new Date()),
        end: FMT_ISO(nextDay(new Date())),
    });

    const onDatesSet = useCallback(
        (arg: DatesSetArg) => {
            const startISO = arg.start.toISOString();
            const endISO = arg.end.toISOString();
            fetchEvents(startISO, endISO);
        },
        [fetchEvents]
    );

    // ---------- modal helpers ----------
    function openEditFromEvent(ev: ApiEvent) {
        // Only Polaris events are editable; others shown read-only
        const readOnly = ev.source?.toLowerCase() !== "polaris" || ev.readOnly === true;
        setDraft({
            id: ev.id,
            source: ev.source,
            title: ev.title,
            description: ev.description || "",
            allDay: !!ev.allDay,
            start: ev.start,
            end: ev.end,
        });
        setModalReadonly(readOnly);
        setModalOpen(true);
    }

    function closeModal() {
        setModalOpen(false);
        setSaving(false);
    }

    async function saveDraft() {
        setSaving(true);
        try {
            // Ensure end is sensible
            const start = new Date(draft.start);
            const end =
                draft.end && new Date(draft.end) > start
                    ? new Date(draft.end)
                    : draft.allDay
                        ? nextDay(start)
                        : plusHours(start, 1);

            const eventData = {
                title: draft.title?.trim() || "(No title)",
                description: draft.description || "",
                allDay: !!draft.allDay,
                start: start.toISOString(),
                end: end.toISOString(),
            };

            const isEdit = isPolarisId(draft.id);
            const numeric = numericIdOrNull(draft.id);

            if (isEdit && numeric !== null) {
                await calendarService.updatePolarisEvent(numeric, eventData);
            } else {
                await calendarService.createPolarisEvent(eventData);
            }

            // refresh
            await refetchEvents();
            setToast("Event saved");
            setTimeout(() => setToast(""), 2500);
            closeModal();
        } catch (e: any) {
            setToast(e?.message || "Failed to save event");
            setTimeout(() => setToast(""), 3000);
            setSaving(false);
        }
    }

    // FullCalendar custom pill
    function renderEventContent(arg: any) {
        if (arg.event.display === "background") {
            return null;
        }
        const props = arg.event.extendedProps as any;
        const src: string | undefined = props?.source;
        const recruitingType: string | undefined = props?.recruitingEventType;
        const recruitingClass = recruitingType ? `pill-recruiting--${recruitingType}` : "";
        const cls = `cal-pill ${src ? `pill-${src}` : ""} ${recruitingClass}`.trim();
        const dotStyle = props?.color ? { background: props.color } : undefined;
        return (
            <div className={cls} title={arg.event.title}>
                <span className="pill-dot" style={dotStyle} />
                <span className="pill-text">{arg.event.title}</span>
            </div>
        );
    }

    // Fetch Google event details
    const fetchGoogleEventDetails = async (eventId: string) => {
        setGoogleEventLoading(true);
        setGoogleEventModalOpen(true);
        setGoogleEventDetails(null);

        try {
            const details: GoogleEventDetails = await calendarService.fetchGoogleEventDetails(eventId);
            setGoogleEventDetails(details);
        } catch (e: any) {
            setToast("Failed to load event details");
            setTimeout(() => setToast(""), 3000);
            setGoogleEventModalOpen(false);
        } finally {
            setGoogleEventLoading(false);
        }
    };

    // Detect click on a recruiting period day cell
    const onDateClick = (info: DateClickArg) => {
        if (!canShowRecruiting || !showRecruiting || recruitingEvents.length === 0) {
            return;
        }

        const match = selectRecruitingEventForDate(
            recruitingEvents,
            utcDateFromCalendarClick(info.date, info.dateStr)
        );
        if (!match) {
            return;
        }

        openEditFromEvent(match);
    };

    const onEventClick = (arg: EventClickArg) => {
        const eventSource = (arg.event.extendedProps as any)?.source;
        const eventId = arg.event.id;

        if (eventSource?.toLowerCase() === "recruiting") {
            const match = selectRecruitingEventForDate(
                recruitingEvents,
                utcDateFromCalendarClick(arg.event.start ?? new Date(), arg.event.startStr)
            );
            if (match) {
                openEditFromEvent(match);
            }
            return;
        }

        // If it's a personal (Google) event, fetch and show full details
        if (eventSource?.toLowerCase() === "personal" && signedInWithGoogle) {
            const googleEventId = extractGoogleEventId(eventId);
            if (googleEventId) {
                fetchGoogleEventDetails(googleEventId);
            }
            return;
        }

        // For company, polaris, or team events, show in the standard modal
        if (
            eventSource?.toLowerCase() === "company"
            || eventSource?.toLowerCase() === "polaris"
            || eventSource?.toLowerCase() === "team"
        ) {
            openEditFromEvent(apiEventFromCalendarClick({
                id: arg.event.id,
                title: arg.event.title,
                startStr: arg.event.startStr,
                endStr: arg.event.endStr,
                allDay: arg.event.allDay,
                extendedProps: arg.event.extendedProps as { source?: string; description?: string | null },
            }));
        }
    };


    const athleteHasSport = athleteSportId != null && athleteSportId > 0;
    const athleteRecruitingLocked = athleteUser && athleteSportReady && !athleteHasSport;
    const adminHasSportSelected = selectedSportId != null && selectedSportId > 0;
    const recruitingLayerActive = canShowRecruiting && showRecruiting;
    const recruitingLegendItems = recruitingLayerActive
        ? buildRecruitingLegendItems(recruitingEvents)
        : [];
    const showEmptyRecruitingMonthHint =
        recruitingLayerActive
        && (adminHasSportSelected || (athleteUser && athleteHasSport))
        && recruitingLegendItems.length === 0
        && !recruitingEventsNotice
        && !recruitingEventsError
        && !isLoading;
    const calendarDisplayEvents = buildCalendarDisplayEvents(
        filteredEvents,
        recruitingEvents,
        recruitingLayerActive
    );

    return (
        <CalendarContent
            loading={isLoading}
            showPolaris={showPolaris}
            setShowPolaris={setShowPolaris}
            showTeam={showTeam}
            setShowTeam={setShowTeam}
            showPersonal={showPersonal}
            setShowPersonal={setShowPersonal}
            showCompany={showCompany}
            setShowCompany={setShowCompany}
            showRecruiting={showRecruiting}
            setShowRecruiting={setShowRecruiting}
            canShowRecruiting={canShowRecruiting}
            athleteUser={athleteUser}
            staffAdminUser={staffAdminUser}
            athleteHasSport={athleteHasSport}
            athleteRecruitingLocked={athleteRecruitingLocked}
            adminHasSportSelected={adminHasSportSelected}
            recruitingChipLabel={recruitingChipLabel}
            recruitingDivision={recruitingDivision}
            onRecruitingDivisionChange={onRecruitingDivisionChange}
            sportComboOptions={sportComboOptions}
            sportComboGroups={sportComboGroups}
            sportsLoading={sportsLoading}
            selectedSportId={selectedSportId}
            setSelectedSportId={setSelectedSportId}
            recruitingLegendItems={recruitingLegendItems}
            showEmptyRecruitingMonthHint={showEmptyRecruitingMonthHint}
            recruitingEventsNotice={recruitingEventsNotice}
            signedInWithGoogle={signedInWithGoogle}
            toast={toast}
            personalEventsError={personalEventsError}
            companyEventsError={companyEventsError}
            recruitingEventsError={recruitingEventsError}
            calendarDisplayEvents={calendarDisplayEvents}
            renderEventContent={renderEventContent}
            onDatesSet={onDatesSet}
            onDateClick={onDateClick}
            onEventClick={onEventClick}
            modalOpen={modalOpen}
            draft={draft}
            setDraft={setDraft}
            modalReadonly={modalReadonly}
            closeModal={closeModal}
            saving={saving}
            saveDraft={saveDraft}
            googleEventModalOpen={googleEventModalOpen}
            googleEventDetails={googleEventDetails}
            googleEventLoading={googleEventLoading}
            setGoogleEventModalOpen={setGoogleEventModalOpen}
        />
    );
}

// Separate component for the calendar content to avoid duplication
type CalendarContentProps = {
    loading: boolean;
    showPolaris: boolean;
    setShowPolaris: (show: boolean) => void;
    showTeam: boolean;
    setShowTeam: (show: boolean) => void;
    showPersonal: boolean;
    setShowPersonal: (show: boolean) => void;
    showCompany: boolean;
    setShowCompany: (show: boolean) => void;
    showRecruiting: boolean;
    setShowRecruiting: (show: boolean) => void;
    canShowRecruiting: boolean;
    athleteUser: boolean;
    staffAdminUser: boolean;
    athleteHasSport: boolean;
    athleteRecruitingLocked: boolean;
    adminHasSportSelected: boolean;
    recruitingChipLabel: string;
    recruitingDivision: RecruitingNcaaDivision;
    onRecruitingDivisionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    sportComboOptions: ComboBoxOption[];
    sportComboGroups: ComboBoxOptionGroup[];
    sportsLoading: boolean;
    selectedSportId: number | null;
    setSelectedSportId: (sportId: number | null) => void;
    recruitingLegendItems: RecruitingLegendItem[];
    showEmptyRecruitingMonthHint: boolean;
    recruitingEventsNotice: string | null;
    signedInWithGoogle: boolean;
    toast: string;
    personalEventsError: string | null;
    companyEventsError: string | null;
    recruitingEventsError: string | null;
    calendarDisplayEvents: Array<Record<string, unknown>>;
    renderEventContent: (arg: unknown) => React.ReactNode;
    onDatesSet: (arg: DatesSetArg) => void;
    onDateClick: (info: DateClickArg) => void;
    onEventClick: (arg: EventClickArg) => void;
    modalOpen: boolean;
    draft: DraftEvent;
    setDraft: React.Dispatch<React.SetStateAction<DraftEvent>>;
    modalReadonly: boolean;
    closeModal: () => void;
    saving: boolean;
    saveDraft: () => Promise<void>;
    googleEventModalOpen: boolean;
    googleEventDetails: GoogleEventDetails | null;
    googleEventLoading: boolean;
    setGoogleEventModalOpen: (open: boolean) => void;
};

function CalendarContent({
    loading,
    showPolaris,
    setShowPolaris,
    showTeam,
    setShowTeam,
    showPersonal,
    setShowPersonal,
    showCompany,
    setShowCompany,
    showRecruiting,
    setShowRecruiting,
    canShowRecruiting,
    athleteUser,
    staffAdminUser,
    athleteHasSport,
    athleteRecruitingLocked,
    adminHasSportSelected,
    recruitingChipLabel,
    recruitingDivision,
    onRecruitingDivisionChange,
    sportComboOptions,
    sportComboGroups,
    sportsLoading,
    selectedSportId,
    setSelectedSportId,
    recruitingLegendItems,
    showEmptyRecruitingMonthHint,
    recruitingEventsNotice,
    signedInWithGoogle,
    toast,
    personalEventsError,
    companyEventsError,
    recruitingEventsError,
    calendarDisplayEvents,
    renderEventContent,
    onDatesSet,
    onDateClick,
    onEventClick,
    modalOpen,
    draft,
    setDraft,
    modalReadonly,
    closeModal,
    saving,
    saveDraft,
    googleEventModalOpen,
    googleEventDetails,
    googleEventLoading,
    setGoogleEventModalOpen,
}: CalendarContentProps) {
    const calendarSectionRef = useRef<HTMLElement>(null);
    const calendarHostRef = useRef<HTMLDivElement>(null);
    const [calendarHeight, setCalendarHeight] = useState<number>();

    useEffect(() => {
        const host = calendarHostRef.current;
        const section = calendarSectionRef.current;
        if (!host) return;

        const updateHeight = () => {
            const nextHeight = Math.floor(host.getBoundingClientRect().height);
            if (nextHeight > 0) {
                setCalendarHeight((prev) => (prev === nextHeight ? prev : nextHeight));
            }
        };

        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(host);
        if (section) {
            observer.observe(section);
        }
        window.addEventListener("resize", updateHeight);
        window.addEventListener("orientationchange", updateHeight);
        window.visualViewport?.addEventListener("resize", updateHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateHeight);
            window.removeEventListener("orientationchange", updateHeight);
            window.visualViewport?.removeEventListener("resize", updateHeight);
        };
    }, []);

    // Google Calendar sync component
    const GoogleCalendarSync = () => {
        const googleHref = `${API_BASE_URL}/oauth2/authorization/google`;

        return (
            <div className="google-sync-box">
                <div className="google-sync-icon">
                    <svg width="48" height="48" viewBox="0 0 48 48">
                        <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                        <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                        <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.30-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                    <span className="google-calendar-text">Google Calendar</span>
                </div>
                <h3 className="google-sync-title">Sync with Google Calendar</h3>
                <p className="google-sync-description">
                    Stay on top of classes, practices, and recruiting meetings by connecting your calendar.
                </p>
                <a href={googleHref} className="btn-google-sync">
                    Sign in with Google
                </a>
            </div>
        );
    };

    return (
        <div className="cal-page">
            <header className="cal-head">
                <div className="cal-filter-stack">
                    <div className="cal-chips">
                        <label className={`chip chip-polaris ${showCompany ? "on" : ""}`}>
                            <input
                                type="checkbox"
                                checked={showCompany}
                                onChange={(e) => setShowCompany(e.target.checked)}
                            />
                            <span>All Polaris</span>
                        </label>
                        {/*<label className={`chip chip-team ${showTeam ? "on" : ""}`}>*/}
                        {/*    <input*/}
                        {/*        type="checkbox"*/}
                        {/*        checked={showTeam}*/}
                        {/*        onChange={(e) => setShowTeam(e.target.checked)}*/}
                        {/*    />*/}
                        {/*    <span>All Team</span>*/}
                        {/*</label>*/}
                        <label
                            className={`chip chip-personal ${showPersonal ? "on" : ""} ${
                                signedInWithGoogle ? "" : "disabled"
                            }`}
                        >
                            <input
                                type="checkbox"
                                disabled={!signedInWithGoogle}
                                checked={showPersonal}
                                onChange={(e) => setShowPersonal(e.target.checked)}
                            />
                            <span>Personal</span>
                        </label>
                        {canShowRecruiting && (
                            <label
                                className={`chip chip-recruiting ${showRecruiting ? "on" : ""} ${
                                    athleteRecruitingLocked ? "disabled" : ""
                                }`}
                                title={
                                    athleteRecruitingLocked
                                        ? "Set your sport in Profile to see recruiting periods"
                                        : "NCAA recruiting periods"
                                }
                            >
                                <input
                                    type="checkbox"
                                    disabled={athleteRecruitingLocked}
                                    checked={showRecruiting}
                                    onChange={(e) => setShowRecruiting(e.target.checked)}
                                />
                                <span>{recruitingChipLabel}</span>
                            </label>
                        )}
                    </div>
                    {canShowRecruiting && showRecruiting && (staffAdminUser || (athleteUser && athleteHasSport)) && (
                        <div className="cal-recruiting-controls">
                            {staffAdminUser && (
                                <div className="cal-recruiting-sport-combobox">
                                    <ComboBox
                                        label="SELECT SPORT"
                                        value={selectedSportId ?? undefined}
                                        options={sportComboOptions}
                                        groupedOptions={
                                            sportComboGroups.length > 0 ? sportComboGroups : undefined
                                        }
                                        onChange={(id) => setSelectedSportId(id ?? null)}
                                        disabled={sportsLoading}
                                        loading={sportsLoading}
                                        placeholder="Search for a sport..."
                                        testId="calendar-recruiting-sport-input"
                                        noOptionsMessage="No sports found"
                                        loadingMessage="Loading sports..."
                                    />
                                </div>
                            )}
                            <fieldset
                                className="cal-recruiting-division"
                                data-testid="calendar-recruiting-division-toggle"
                            >
                                <legend className="cal-recruiting-division-legend">NCAA DIVISION</legend>
                                <div className="cal-recruiting-division-options" role="radiogroup" aria-label="NCAA division">
                                    <label className={`cal-recruiting-division-option ${recruitingDivision === "D1" ? "on" : ""}`}>
                                        <input
                                            type="radio"
                                            name="ncaa-recruiting-division"
                                            value="D1"
                                            checked={recruitingDivision === "D1"}
                                            onChange={onRecruitingDivisionChange}
                                        />
                                        Division I
                                    </label>
                                    <label className={`cal-recruiting-division-option ${recruitingDivision === "D2" ? "on" : ""}`}>
                                        <input
                                            type="radio"
                                            name="ncaa-recruiting-division"
                                            value="D2"
                                            checked={recruitingDivision === "D2"}
                                            onChange={onRecruitingDivisionChange}
                                        />
                                        Division II
                                    </label>
                                </div>
                            </fieldset>
                            {recruitingLegendItems.length > 0 && (
                                <div className="cal-recruiting-legend" aria-label="NCAA recruiting period legend">
                                    {recruitingLegendItems.map((item) => (
                                        <span key={item.slug} className="cal-recruiting-legend-item">
                                            <span
                                                className="cal-recruiting-legend-dot"
                                                style={{ backgroundColor: item.color }}
                                            />
                                            {item.label}: {item.rangeLabel}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {staffAdminUser && !adminHasSportSelected && (
                                <p className="cal-recruiting-hint">
                                    Choose a sport to display recruiting periods on the calendar.
                                </p>
                            )}
                            {showEmptyRecruitingMonthHint && (
                                <p
                                    className="cal-recruiting-hint"
                                    data-testid="calendar-recruiting-empty-month"
                                >
                                    {EMPTY_RECRUITING_MONTH_HINT}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </header>

            {toast && <div className="cal-toast">{toast}</div>}
            {personalEventsError && <div className="cal-error">Personal Calendar: {personalEventsError}</div>}
            {companyEventsError && <div className="cal-error">Company Calendar: {companyEventsError}</div>}
            {recruitingEventsNotice && (
                <div className="cal-recruiting-notice" role="status">
                    {recruitingEventsNotice}
                </div>
            )}
            {recruitingEventsError && (
                <div className="cal-error">Sport Events Calendar: {recruitingEventsError}</div>
            )}

            <section className="cal-calendar-section" ref={calendarSectionRef}>
                <div className="cal-calendar-host" ref={calendarHostRef}>
                    <div className={`cal-calendar-container ${loading ? "is-loading" : ""}`}>
                        <FullCalendar
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            headerToolbar={{
                                left: "prev,next",
                                center: "title",
                                right: "",
                            }}
                            height={calendarHeight ?? "auto"}
                            expandRows={true}
                            fixedWeekCount={true}
                            dayMaxEventRows={2}
                            moreLinkClick="popover"
                            eventContent={renderEventContent}
                            events={calendarDisplayEvents}
                            datesSet={onDatesSet}
                            dateClick={onDateClick}
                            eventClick={onEventClick}
                        />
                    </div>
                </div>
            </section>

            <div className="cal-bottom-row">
                <WeeklySnapshot />
                {!signedInWithGoogle && <GoogleCalendarSync />}
            </div>

            {modalOpen && (
                <div
                    className="cal-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cal-modal-title"
                    data-testid="calendar-event-modal"
                >
                    <div className="cal-modal-card">
                        <div className="cal-modal-head">
                            <h3 id="cal-modal-title">
                                {modalReadonly && !isPolarisId(draft.id)
                                    ? "Event Details"
                                    : isPolarisId(draft.id)
                                        ? "Edit Event"
                                        : "Add Event"}
                                {modalReadonly && isPolarisId(draft.id) ? " (read-only)" : ""}
                            </h3>
                            <button className="cal-x" onClick={closeModal} aria-label="Close">×</button>
                        </div>

                        <div className="cal-modal-body">
                            <div className="cal-field">
                                <label>Title</label>
                                <input
                                    className="cal-input"
                                    value={draft.title}
                                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                                    disabled={modalReadonly}
                                />
                            </div>

                            <div className="cal-grid2">
                                <label className="cal-check">
                                    <input
                                        type="checkbox"
                                        checked={draft.allDay}
                                        onChange={(e) => setDraft({ ...draft, allDay: e.target.checked })}
                                        disabled={modalReadonly}
                                    />
                                    <span>All day</span>
                                </label>
                            </div>

                            <div className="cal-grid2">
                                <div className="cal-field">
                                    <label>Start</label>
                                    {modalReadonly && draft.source === "recruiting" ? (
                                        <input
                                            className="cal-input"
                                            readOnly
                                            value={formatUsCalendarDate(draft.start)}
                                            data-testid="calendar-recruiting-date-start"
                                        />
                                    ) : (
                                        <input
                                            className="cal-input"
                                            type="datetime-local"
                                            value={toLocalInputValue(draft.start)}
                                            onChange={(e) =>
                                                setDraft({ ...draft, start: fromLocalInputValue(e.target.value) })
                                            }
                                            disabled={modalReadonly}
                                        />
                                    )}
                                </div>
                                <div className="cal-field">
                                    <label>End</label>
                                    {modalReadonly && draft.source === "recruiting" ? (
                                        <input
                                            className="cal-input"
                                            readOnly
                                            value={formatUsCalendarDate(inclusiveRecruitingEndIso(draft.start, draft.end))}
                                            data-testid="calendar-recruiting-date-end"
                                        />
                                    ) : (
                                        <input
                                            className="cal-input"
                                            type="datetime-local"
                                            value={toLocalInputValue(draft.end || draft.start)}
                                            onChange={(e) =>
                                                setDraft({ ...draft, end: fromLocalInputValue(e.target.value) })
                                            }
                                            disabled={modalReadonly}
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="cal-field">
                                <label>Description</label>
                                {modalReadonly ? (
                                    <div
                                        className="cal-description-content"
                                        dangerouslySetInnerHTML={{ __html: sanitizeAndLinkifyDescription(draft.description) }}
                                    />
                                ) : (
                                    <textarea
                                        className="cal-textarea"
                                        rows={4}
                                        value={draft.description || ""}
                                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                        disabled={modalReadonly}
                                    />
                                )}
                            </div>

                            {modalReadonly && draft.source?.toLowerCase() === "personal" && (
                                <p className="cal-help">
                                    Google events are read-only here. Edit them in Google Calendar.
                                </p>
                            )}
                            {modalReadonly && draft.source?.toLowerCase() === "company" && (
                                <p className="cal-help">
                                    Company calendar events are read-only.
                                </p>
                            )}
                            {modalReadonly && draft.source?.toLowerCase() === "recruiting" && (
                                <p className="cal-help">
                                    NCAA recruiting calendar periods are read-only.
                                </p>
                            )}
                        </div>

                        <div className="cal-modal-actions">
                            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
                            {!modalReadonly && (
                                <button className="btn-pill" onClick={saveDraft} disabled={saving}>
                                    {saving ? "Saving…" : "Save"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {googleEventModalOpen && (
                <GoogleEventDetailsModal
                    event={googleEventDetails}
                    loading={googleEventLoading}
                    onClose={() => setGoogleEventModalOpen(false)}
                />
            )}
        </div>
    );
}

/** Helpers to bind <input type="datetime-local"> to ISO (UTC) strings */
function toLocalInputValue(iso: string) {
    try {
        const d = new Date(iso);
        const pad = (n: number) => n.toString().padStart(2, "0");
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const hh = pad(d.getHours());
        const mi = pad(d.getMinutes());
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    } catch {
        return "";
    }
}
function fromLocalInputValue(local: string) {
    // interpret as local time and convert to ISO
    const d = new Date(local);
    return d.toISOString();
}
